import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  isPaymentsConfigured,
  sendVerificationEmailApi,
  looksLikeUsername,
  resolveUsernameApi,
  sendPasswordResetEmailApi,
  lookupOrgByCode,
  checkAdminSignupCode,
} from '../services/paymentsApi';
import { FLEET_ORG_PRODUCT_TYPES } from '../fleet/fleetConstants';
import AuthFrame, { AuthError } from '../site-v3/AuthFrame';

/** Does this org doc name `email` on one of the lists the users-doc rule
 *  accepts for a fleet_admin who is not its adminUid (orgNamesCaller in
 *  pear_mobile/firestore.rules)? A brand-new account is never the adminUid, so
 *  for this page these three lists are the only way in. */
const orgNamesAdminEmail = (org: Record<string, unknown>, email: string): boolean => {
  const lower = (v: unknown) => String(v ?? '').trim().toLowerCase();
  const list = (v: unknown) => (Array.isArray(v) ? v.map(lower) : []);
  return lower(org.adminEmail) === email
    || list(org.adminEmails).includes(email)
    || list(org.pendingAdminInvites).includes(email);
};

/** The users write was refused. Pressing again changes nothing until something
 *  changes, so this says so instead of promising a retry will work
 *  (2026-09-15). Usually the rules found this address on none of the org's
 *  admin lists — but a refusal also comes from an org with no doc under this
 *  exact code, or an account that already has a users doc, so it says
 *  "usually" rather than claiming the cause. */
const notOnAdminListMessage = (org: string) =>
  `We could not set this account up for ${org}. Usually that means this email is not on ${org}'s admin list yet: ask your organisation's admin or PEAR support to add it, then press Complete sign up.`;

/** Move an invitee from the org's pendingAdminInvites into adminEmails, as
 *  claimAdminInvite (selfServeSignup.ts) does after the email link. Never throws.
 *
 *  2026-09-15: the users doc makes this person fleet_admin, but the backend's
 *  strict gate (addons.userAdministersOrgStrict: Create driver, the school
 *  admin app's Cancel / Undo) reads the ORG doc and deliberately ignores
 *  pendingAdminInvites. An invitee who signed up here with a one-time code
 *  instead of opening their invite link stayed named only there, and was
 *  refused as "not on this organisation's admin list" for good. Same write
 *  shape claimAdminInvite sends, so the same invite-accept clause allows it
 *  (callerEmailVerified: the caller has already refreshed its token). Skipped
 *  when the invite list does not hold the address — an org that names them by
 *  adminEmail / adminEmails already passes the gate, and a write the rules
 *  would refuse is not worth sending. */
const claimPendingAdminInvite = async (org: string, rawEmail: string | null) => {
  const invited = (rawEmail || '').trim().toLowerCase();
  if (!invited) return;
  try {
    const orgRef = doc(db, 'organizations', org);
    const snap = await getDoc(orgRef);
    const pending = snap.exists() ? (snap.data()?.pendingAdminInvites as unknown) : null;
    if (!Array.isArray(pending) || !pending.map((e) => String(e).toLowerCase()).includes(invited)) return;
    await updateDoc(orgRef, {
      adminEmails: arrayUnion(invited),
      pendingAdminInvites: arrayRemove(invited),
      updatedAt: new Date(),
    });
  } catch {
    // Refused or offline. The users doc already stands and sign-in still works;
    // opening the emailed invite runs claimAdminInvite, which retries this move.
  }
};

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  /* Only the two jobs the new sign-in page sends here: create your account
     and reset your password. Signing in itself is /sign-in (2026-10-28). */
  const [mode, setMode] = useState<'create' | 'forgot'>(() => (searchParams.get('mode') === 'forgot' ? 'forgot' : 'create'));
  const navigate = useNavigate();
  /** Sign-in accepts either form, so this is not necessarily an email. Kept apart
   *  from `email` below, which stays a strict email for account creation. */
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [adminCode, setAdminCode] = useState('');
  const [orgCodeValid, setOrgCodeValid] = useState<boolean | null>(null);
  const [orgCodeError, setOrgCodeError] = useState('');
  const [adminCodeValid, setAdminCodeValid] = useState<boolean | null>(null);
  const [adminCodeError, setAdminCodeError] = useState('');
  const [adminCodeOrgCode, setAdminCodeOrgCode] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'verify' | 'success'>('form');
  const [pendingOrgCode, setPendingOrgCode] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendCooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** "Complete sign up" and the 5 s poll run the same finish. One at a time:
   *  otherwise one signs out while the other is still writing, and its
   *  permission error is left behind on the login form. */
  const completingRef = useRef(false);

  useEffect(() => {
    if (searchParams.get('mode') === 'signup') {
      setMode('create');
    } else if (searchParams.get('mode') === 'forgot') {
      // The site's Sign in page links "Forgot?" here (2026-10-23).
      setMode('forgot');
    }
  }, [searchParams]);

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const typed = identifier.trim();
    if (!typed) {
      setError('Enter your email or username.');
      return;
    }
    try {
      setError('');
      setLoading(true);
      let emailToUse = typed;
      if (looksLikeUsername(typed)) {
        const resolved = await resolveUsernameApi(typed);
        if (!resolved.ok || !resolved.email) {
          setError(resolved.error || 'That username was not recognised.');
          return;
        }
        emailToUse = resolved.email;
      }
      const result = await sendPasswordResetEmailApi(emailToUse);
      if (!result.ok) {
        setError(result.error || 'Could not send reset email.');
        return;
      }
      setResetSentTo(emailToUse);
    } catch (err: any) {
      setError(err?.message || 'Could not send reset email.');
    } finally {
      setLoading(false);
    }
  };

  const validateOrgCode = async (code: string): Promise<{ valid: boolean; orgName?: string; orgCode?: string }> => {
    const trimmed = code.trim();
    if (!trimmed) return { valid: false };
    const looked = await lookupOrgByCode(trimmed);
    if (!looked.ok || !looked.org) return { valid: false };
    const pt = (looked.org.productType || '').toUpperCase();
    if (pt && !(FLEET_ORG_PRODUCT_TYPES as readonly string[]).includes(pt)) return { valid: false };
    return { valid: true, orgName: looked.org.name || looked.org.code, orgCode: looked.org.code };
  };

  /** `orgCode` is the one validateOrgCode resolved: the code is checked FOR it. */
  const validateOneTimeCode = async (code: string, orgCode: string): Promise<{ valid: boolean; orgName?: string; orgCode?: string; org?: Record<string, unknown> }> => {
    const trimmed = code.trim();
    if (!trimmed || !/^\d{4}$/.test(trimmed) || !orgCode.trim()) return { valid: false };
    /* 2026-09-17: this LISTED fleet_admin_signup_codes where code == the digits,
       signed out — so anyone could list the whole collection. The rules keep it
       to PEAR admins; the server says whether these are the live code for THIS
       org (the digits alone name none). A check that could not be made throws,
       so both callers say why instead of calling a good code invalid. */
    const checked = await checkAdminSignupCode(orgCode, trimmed);
    if (checked.state === 'unknown') {
      throw new Error('Could not check that code right now. Check your connection and try again.');
    }
    if (checked.state === 'too_many') {
      throw new Error('Too many wrong codes were tried from this network. Wait 15 minutes, then try again.');
    }
    if (checked.state === 'busy') {
      throw new Error('Too many wrong codes have been tried for this organization. Ask your Trasse admin for a new code, then enter it.');
    }
    if (checked.state !== 'valid') return { valid: false };
    const codeOrgCode = checked.orgCode;
    const orgSnap = await getDoc(doc(db, 'organizations', codeOrgCode));
    const org = orgSnap.exists() ? (orgSnap.data() as Record<string, unknown>) : undefined;
    const orgName = (org?.name as string | undefined) || codeOrgCode;
    return { valid: true, orgName, orgCode: codeOrgCode, org };
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const emailVal = email.trim().toLowerCase();
    const orgTrimmed = orgCode.trim();
    const adminTrimmed = adminCode.trim();
    if (!emailVal || !password || !confirmPassword || !orgTrimmed || !adminTrimmed) {
      setError('Please fill in all fields');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      const orgResult = await validateOrgCode(orgTrimmed);
      if (!orgResult.valid) {
        setError('Invalid organization code. Use your fleet org code (e.g. FLEET1.fleet, HAULAGE1.cargo, or legacy FLEET1.bus / HAULAGE1.lorry).');
        setLoading(false);
        return;
      }
      const resolvedOrgCode = orgResult.orgCode || orgTrimmed;
      const adminResult = await validateOneTimeCode(adminTrimmed, resolvedOrgCode);
      if (!adminResult.valid) {
        setError('Invalid or expired one-time code. Ask your Trasse admin for a new code.');
        setLoading(false);
        return;
      }
      if ((adminResult.orgCode ?? '').toLowerCase() !== resolvedOrgCode.toLowerCase()) {
        setError('One-time code is not for this organization.');
        setLoading(false);
        return;
      }
      const canonicalOrgCode = adminResult.orgCode ?? resolvedOrgCode;
      /* 2026-09-15: the one-time code names nobody — Dashboard's "Generate
         one-time code" writes no address onto the org — and the users write
         that ends this signup is allowed only for an address the org already
         lists. Someone it did not list got an Auth account, verified it, was
         refused, and was stuck: "Create account" says the email is taken and
         login signs them out. Stop before the account exists. A missing org
         doc skips the check (a refused read throws above); the rules still
         decide. */
      if (adminResult.org && !orgNamesAdminEmail(adminResult.org, emailVal)) {
        setError(`${emailVal} is not on ${adminResult.orgName || canonicalOrgCode}'s admin list. Ask your organisation's admin to invite this address, or PEAR support to add it, then create your account.`);
        return;
      }
      await createUserWithEmailAndPassword(auth, emailVal, password);
      if (isPaymentsConfigured()) {
        const branded = await sendVerificationEmailApi(window.location.origin);
        if (!branded.ok) {
          setError(branded.error || 'Account created but verification email failed. Use Resend below.');
        }
      } else {
        setError('Account created but email service is not configured. Contact support.');
      }
      setPendingOrgCode(canonicalOrgCode);
      setStep('verify');
    } catch (err: any) {
      const code = err?.code;
      let msg = err?.message || 'Failed to create account';
      if (code === 'auth/email-already-in-use') msg = 'An account with this email already exists. Log in instead.';
      else if (code === 'auth/quota-exceeded' || code === 'auth/too-many-requests') msg = 'Sign-up limit reached for today. Please try again tomorrow.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  /** The users doc for an account reload() has just shown verified, then the
   *  success step. "Complete sign up" and the 5 s poll both finish here, so
   *  they write one doc — the poll's copy had no orgCode. Throws. */
  const finishVerifiedSignup = async (user: NonNullable<typeof auth.currentUser>, org: string) => {
    /* 2026-09-14: reload() refreshes the User object, not the ID token the
       Firestore rules read. The token createUserWithEmailAndPassword handed
       back still says email_verified false, and the rules refuse a
       fleet_admin write proven only by an unverified email — so the person
       was left with an Auth account and no users doc: login signs them out
       and "Create account" says the email is taken. */
    await user.getIdToken(true);
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: user.email?.split('@')[0] || '',
      role: 'fleet_admin',
      schoolCode: org,
      institution: org,
      organizationCode: org,
      orgCode: org,
      profilePicUrl: null,
      createdAt: serverTimestamp(),
      isFleetAdmin: true,
      emailVerified: true,
      signupComplete: true,
    });
    // After the users doc, before signOut: it needs the refreshed token, and it
    // never throws, so a refused org update cannot cost them the account.
    await claimPendingAdminInvite(org, user.email);
    await signOut(auth);
    setPendingOrgCode(null);
    setStep('success');
  };

  const handleCompleteSignup = async () => {
    setError('');
    const user = auth.currentUser;
    if (!user || !pendingOrgCode) {
      setError('Session expired. Please start over.');
      return;
    }
    if (completingRef.current) return;
    completingRef.current = true;
    try {
      setLoading(true);
      await user.reload();
      const refreshed = auth.currentUser;
      if (!refreshed?.emailVerified) {
        setError('Please verify your email first. Click the link we sent to your inbox, then try again.');
        return;
      }
      await finishVerifiedSignup(refreshed, pendingOrgCode);
    } catch (err: any) {
      setError(err?.code === 'permission-denied'
        ? notOnAdminListMessage(pendingOrgCode)
        : (err?.message || 'Failed to complete sign up'));
    } finally {
      completingRef.current = false;
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const user = auth.currentUser;
    if (!user || resendCooldown > 0) return;
    setError('');
    try {
      if (!isPaymentsConfigured()) {
        setError('Email service is not configured.');
        return;
      }
      const branded = await sendVerificationEmailApi(window.location.origin);
      if (!branded.ok) {
        setError(branded.error || 'Failed to resend verification email.');
        return;
      }
      if (resendCooldownRef.current) clearInterval(resendCooldownRef.current);
      setResendCooldown(60);
      resendCooldownRef.current = setInterval(() => {
        setResendCooldown((s) => {
          if (s <= 1) {
            if (resendCooldownRef.current) {
              clearInterval(resendCooldownRef.current);
              resendCooldownRef.current = null;
            }
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } catch (err: any) {
      setError(err?.message || 'Failed to resend. Try again in a few minutes.');
    }
  };

  useEffect(() => {
    if (step !== 'verify' || !auth.currentUser || !pendingOrgCode) return;
    const interval = setInterval(async () => {
      const user = auth.currentUser;
      if (!user || !pendingOrgCode || completingRef.current) return;
      try {
        await user.reload();
      } catch (_) {
        return; // offline for a tick — the next one asks again
      }
      const fresh = auth.currentUser;
      // Signed out, or "Complete sign up" took over while reload() ran.
      if (!fresh || fresh.uid !== user.uid || !fresh.emailVerified || completingRef.current) return;
      completingRef.current = true;
      setLoading(true);
      try {
        await finishVerifiedSignup(fresh, pendingOrgCode);
      } catch (err: any) {
        /* 2026-09-14: this cleared the interval before writing and swallowed a
           failed write, so a refused write left a verified person on "Check
           your inbox" with nothing said and nothing retrying. It still stops
           rather than rewriting every 5 s — the button is the retry. A refusal
           gets its own message (2026-09-15): pressing again cannot pass until
           the org lists the address, and "try again" sent people round a loop. */
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setError(err?.code === 'permission-denied'
          ? notOnAdminListMessage(pendingOrgCode)
          : `${err?.message || 'Could not finish creating your account.'} Press Complete sign up to try again.`);
      } finally {
        completingRef.current = false;
        setLoading(false);
      }
    }, 5000);
    pollRef.current = interval;
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [step, pendingOrgCode]);

  useEffect(() => {
    return () => {
      if (resendCooldownRef.current) clearInterval(resendCooldownRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  /** Every "back to sign in" goes to the new page. An unverified account
   *  half-way through signing up is signed out first, as before. */
  const toSignIn = () => {
    if (step === 'verify' && auth.currentUser && !auth.currentUser.emailVerified) {
      signOut(auth);
    }
    navigate('/sign-in');
  };

  const clearAdminCodeCheck = () => {
    setAdminCodeValid(null);
    setAdminCodeError('');
    setAdminCodeOrgCode(null);
  };

  /* The one-time code is checked FOR an org (2026-09-17): the digits alone name
     none. So it waits for a valid org code, and is checked again whenever the
     org code is. adminCodeOrgCode keeps the org code AS TYPED at the check, so
     orgMatch holds for a legacy code (FLEET1.bus) that resolves to another. */
  const checkAdminCodeFor = async (orgTyped: string, resolvedOrgCode: string, adminTyped: string) => {
    try {
      const result = await validateOneTimeCode(adminTyped, resolvedOrgCode);
      setAdminCodeValid(result.valid);
      setAdminCodeError(result.valid ? '' : 'Invalid or expired.');
      setAdminCodeOrgCode(result.valid ? orgTyped : null);
    } catch (e) {
      setAdminCodeValid(false);
      // The reason, when there is one: "could not check" is not "invalid".
      setAdminCodeError(e instanceof Error && e.message ? e.message : 'Could not verify.');
      setAdminCodeOrgCode(null);
    }
  };

  const handleAdminCodeBlur = async () => {
    const trimmed = adminCode.trim();
    const orgTyped = orgCode.trim();
    if (!trimmed || !/^\d{4}$/.test(trimmed) || !orgTyped) {
      clearAdminCodeCheck();
      return;
    }
    try {
      const org = await validateOrgCode(orgTyped);
      // An org that does not resolve is the org field's message, not this one's.
      if (!org.valid || !org.orgCode) {
        clearAdminCodeCheck();
        return;
      }
      await checkAdminCodeFor(orgTyped, org.orgCode, trimmed);
    } catch {
      setAdminCodeValid(false);
      setAdminCodeError('Could not verify.');
      setAdminCodeOrgCode(null);
    }
  };

  const handleOrgCodeBlur = async () => {
    const trimmed = orgCode.trim();
    if (!trimmed) {
      setOrgCodeValid(null);
      setOrgCodeError('');
      return;
    }
    try {
      const result = await validateOrgCode(trimmed);
      setOrgCodeValid(result.valid);
      setOrgCodeError(result.valid ? '' : 'Organization not found.');
      const admin = adminCode.trim();
      if (result.valid && result.orgCode && /^\d{4}$/.test(admin)) {
        await checkAdminCodeFor(trimmed, result.orgCode, admin);
      }
    } catch {
      setOrgCodeValid(false);
      setOrgCodeError('Could not verify.');
    }
  };

  const orgMatch = adminCodeOrgCode != null && orgCode.trim() && adminCodeOrgCode.toLowerCase() === orgCode.trim().toLowerCase();
  const canSubmit =
    !!email.trim() &&
    password.length >= 8 &&
    confirmPassword === password &&
    !!orgCode.trim() &&
    !!adminCode.trim() &&
    orgCodeValid === true &&
    adminCodeValid === true &&
    orgMatch;

  /* The new site's look (AuthFrame): the same frame, type and fields as
     /sign-in, so creating an account or resetting a password no longer drops
     a school admin into the old dark admin console (Gabriel, 2026-10-28). */
  const eye = (
    <button
      type="button"
      onClick={togglePasswordVisibility}
      aria-label={showPassword ? 'Hide password' : 'Show password'}
      style={{ position: 'absolute', right: 6, top: 6, width: 36, height: 36, border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--muted)', display: 'grid', placeItems: 'center' }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        {showPassword ? (
          <>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <path d="m1 1 22 22" />
          </>
        ) : (
          <>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </>
        )}
      </svg>
    </button>
  );
  const note = (text: React.ReactNode, color = 'var(--muted)', weight = 400) => (
    <span className="xs" style={{ color, fontWeight: weight }}>{text}</span>
  );
  const okNote = (text: string) => note(text, '#027A48', 600);
  const badNote = (text: string) => note(text, '#B42318', 600);
  const btn: React.CSSProperties = { justifyContent: 'center', border: 0, font: 'inherit', fontWeight: 600, width: '100%', cursor: 'pointer' };
  const busyBtn = (busy: boolean): React.CSSProperties => ({ ...btn, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 });
  const backLine = (lead: string) => (
    <p className="xs" style={{ margin: 0, color: 'var(--muted)' }}>
      {lead}{' '}
      <a className="lnk" href="/sign-in" onClick={(e) => { e.preventDefault(); toSignIn(); }}>Sign in</a>.
    </p>
  );
  /** A finished step: a tile, the headline, a sentence, its buttons. */
  const status = (icon: React.ReactNode, title: string, body: React.ReactNode, actions: React.ReactNode) => (
    <div style={{ display: 'grid', gap: 16 }}>
      <span style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--s2)', color: 'var(--teal-ink)', display: 'grid', placeItems: 'center' }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{icon}</svg>
      </span>
      <h1 className="d3">{title}</h1>
      <p className="p sm" style={{ margin: 0 }}>{body}</p>
      <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>{actions}</div>
    </div>
  );
  const mailIcon = <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>;
  const tickIcon = <path d="m5 12 5 5 9-10" />;

  let body: React.ReactNode;
  if (mode === 'forgot' && resetSentTo) {
    body = status(
      mailIcon,
      'Check your inbox',
      <>We sent a reset link to <b style={{ color: 'var(--ink)' }}>{resetSentTo}</b>. Open it to choose a new password, then sign in. If it isn’t there in a minute, look in spam.</>,
      <>
        <button type="button" className="btn btn-k" style={btn} onClick={toSignIn}>Back to sign in</button>
        <button type="button" className="btn btn-s" style={btn} onClick={() => { setResetSentTo(null); setError(''); }} disabled={loading}>
          Send to a different address
        </button>
        {error && <AuthError>{error}</AuthError>}
      </>,
    );
  } else if (mode === 'forgot') {
    body = (
      <>
        <h1 className="d3">Reset your password</h1>
        <p className="p sm" style={{ marginTop: 10 }}>Type the email or username of your account and we’ll send you a link to choose a new password.</p>
        <form onSubmit={handleForgotSubmit} noValidate style={{ display: 'grid', gap: 16, marginTop: 28 }}>
          <label className="field" htmlFor="forgot-identifier">
            <span>Email or username</span>
            <input
              type="text"
              id="forgot-identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="grace@kiota.ac.ke"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className={error ? 'ferr' : undefined}
              aria-invalid={!!error}
              autoFocus
            />
          </label>
          {error && <AuthError>{error}</AuthError>}
          <button type="submit" className="btn btn-k" style={busyBtn(loading)} disabled={loading}>
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
          {backLine('Remembered it?')}
        </form>
      </>
    );
  } else if (step === 'verify') {
    body = status(
      mailIcon,
      'Check your inbox',
      <>We sent a link to <b style={{ color: 'var(--ink)' }}>{email}</b>. Open it, then come back and press <b style={{ color: 'var(--ink)' }}>Complete sign up</b>.</>,
      <>
        <button type="button" className="btn btn-k" style={busyBtn(loading)} onClick={handleCompleteSignup} disabled={loading}>
          {loading ? 'Completing…' : 'Complete sign up'}
        </button>
        <button type="button" className="btn btn-s" style={busyBtn(resendCooldown > 0)} onClick={handleResendVerification} disabled={resendCooldown > 0}>
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend the email'}
        </button>
        {error && <AuthError>{error}</AuthError>}
      </>,
    );
  } else if (step === 'success') {
    body = status(
      tickIcon,
      'Account created',
      'Your account is ready. Sign in with your email and password.',
      <button type="button" className="btn btn-k" style={btn} onClick={toSignIn}>Sign in</button>,
    );
  } else {
    body = (
      <>
        <h1 className="d3">Create your account</h1>
        <p className="p sm" style={{ marginTop: 10 }}>
          For the admin of a school or fleet already on Trasse. You need your school or fleet code and the 4-digit code from Trasse.
        </p>
        <form onSubmit={handleCreateAccount} noValidate style={{ display: 'grid', gap: 16, marginTop: 28 }}>
          <label className="field" htmlFor="create-email">
            <span>Email address</span>
            <input type="email" id="create-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="grace@kiota.ac.ke" autoComplete="email" required />
          </label>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="field" htmlFor="create-org">
              <span>Organization code</span>
              <input
                type="text"
                id="create-org"
                value={orgCode}
                onChange={(e) => { setOrgCode(e.target.value); setOrgCodeValid(null); setOrgCodeError(''); clearAdminCodeCheck(); }}
                onBlur={handleOrgCodeBlur}
                placeholder="LEAF.bus"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className={orgCodeValid === false ? 'ferr' : undefined}
                required
              />
            </label>
            {orgCodeValid === true
              ? okNote('✓ Organization verified')
              : orgCodeValid === false
                ? badNote(orgCodeError || 'Invalid')
                : note('The code Trasse gave your school, like LEAF.bus.')}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="field" htmlFor="create-admin-code">
              <span>One-time admin code (4 digits)</span>
              <input
                type="text"
                id="create-admin-code"
                inputMode="numeric"
                maxLength={4}
                value={adminCode}
                onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 4); setAdminCode(v); setAdminCodeValid(null); setAdminCodeError(''); setAdminCodeOrgCode(null); }}
                onBlur={handleAdminCodeBlur}
                placeholder="4-digit code"
                className={adminCodeValid === false ? 'ferr' : undefined}
                required
              />
            </label>
            {adminCodeValid === true
              ? okNote('✓ Code valid')
              : adminCodeValid === false
                ? badNote(adminCodeError || 'Invalid or expired')
                : note('From Trasse. It works for 15 minutes.')}
          </div>
          <label className="field" htmlFor="create-password">
            <span>Password</span>
            <div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} id="create-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" required minLength={8} style={{ width: '100%', paddingRight: 48 }} />
              {eye}
            </div>
          </label>
          <label className="field" htmlFor="create-confirm">
            <span>Confirm password</span>
            <input type={showPassword ? 'text' : 'password'} id="create-confirm" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Type it again" autoComplete="new-password" required />
          </label>
          {error && <AuthError>{error}</AuthError>}
          <button type="submit" className="btn btn-k" style={busyBtn(loading || !canSubmit)} disabled={loading || !canSubmit}>
            {loading ? 'Sending verification…' : 'Send verification email'}
          </button>
          {backLine('Already have an account?')}
        </form>
      </>
    );
  }

  return <AuthFrame>{body}</AuthFrame>;
};

export default Login;
